import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Flex, QRCode, Spin } from 'antd';
import { CopyOutlined, RedoOutlined } from '@ant-design/icons';
import useApiClient from '../../hooks/api/useApiClient';
import ClickToCopy from '../ClickToCopy';

export default function ContractPayment({ data, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:ContractPayment' });

  const { ServiceContractsManager } = useApiClient();

  const [paymentLink, setPaymentLink] = useState(null);
  const [loadingPaymentLink, setLoadingPaymentLink] = useState(false);

  useEffect(() => {
    data?.is_immediate_payment_required && createPaymentLink(data.id);
  }, [data?.is_immediate_payment_required]);

  const createPaymentLink = async (contractId) => {
    setLoadingPaymentLink(true);
    const { success, data: responseData } = await ServiceContractsManager.createPaymentLink(contractId, {
      return_url: window.location.href,
    });
    setLoadingPaymentLink(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_create_payment_link'));
    }

    return setPaymentLink(responseData.checkout_url);
  };

  if (!data?.is_immediate_payment_required) {
    return null;
  }

  return (
    <div className={className}>
      <h5 className="mx-auto">{t('sections.payment')}</h5>
      <Flex gap="middle" className="mt-md">
        <div>
          <QRCode
            value={paymentLink ?? 'https://google.com'}
            status={loadingPaymentLink ? 'loading' : 'active'}
            className="w-full"
          />
          <Button
            type="text"
            color="primary"
            className="mt-md"
            icon={<RedoOutlined className="-rotate-90" />}
            loading={loadingPaymentLink}
            onClick={() => createPaymentLink(data.id)}
          >
            {t('buttons.generate_new_link')}
          </Button>
        </div>
        <div className="w-full overflow-hidden relative">
          <Flex gap="small">
            <p className="my-auto">{t('labels.proceed_to_payment')}</p>
            {Boolean(paymentLink) && !loadingPaymentLink && (
              <ClickToCopy value={paymentLink} noIcon textClassName="my-auto">
                <Button type="text" icon={<CopyOutlined />}>
                  {t('buttons.copy_link')}
                </Button>
              </ClickToCopy>
            )}
          </Flex>
          {loadingPaymentLink ? (
            <Spin />
          ) : (
            <p className="mt-md overflow-hidden text-ellipsis text-brand-primary hover:underline">
              <a href={paymentLink} target="_blank" rel="noopener noreferrer">
                {paymentLink}
              </a>
            </p>
          )}
        </div>
        {Boolean(paymentLink) && (
          <Button
            type="primary"
            size="large"
            disabled={loadingPaymentLink}
            onClick={() => window.open(paymentLink, '_blank')}
            className="absolute right-lg bottom-lg px-xl"
          >
            {t('buttons.pay_now')}
          </Button>
        )}
      </Flex>
    </div>
  );
}
